import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { RotateCw, Star } from "react-feather";
import { C } from "../reporting/reportingStyles";
import { Badge } from "../reporting/ReportingShared";

const headerStyle = { borderBottom: `2px solid ${C.borderLight}` };
const bodyStyle = { padding: 24, fontFamily: "'Inter', sans-serif" };
const footerStyle = { borderTop: `1px solid ${C.borderLight}`, gap: 8 };
const coralBtnStyle = { background: C.coral, border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13 };
const ghostBtnStyle = { background: C.bg, border: `1px solid ${C.border}`, color: C.text, borderRadius: 10, fontWeight: 600, fontSize: 13 };

const STATUS_META = {
  pending: { label: "Waiting", color: C.orange, bg: C.orangeLight },
  success: { label: "Synced", color: "#0F7B6C", bg: C.coralLight },
  stale: { label: "Stale", color: "#B42318", bg: "#FEE4E2" },
  failed: { label: "Failed", color: "#B42318", bg: "#FEE4E2" },
};

/**
 * Read-only detail for one feedback entry.
 *
 * Nothing here is editable on purpose: a customer's rating and words are a record of what
 * they said, and a screen that can quietly reword them is worse than no screen. What this
 * adds over the table row is the parts the table has to truncate — the full comment, the
 * full order uid, the full push error — plus the sync timeline, which is the only place to
 * see WHEN the next retry is due and which Odoo review it became.
 */
const FeedbackDetailModal = ({ isOpen, toggle, row, onRetry, syncing }) => {
  if (!row) return null;

  const meta = STATUS_META[row.pushStatus] || STATUS_META.pending;
  const retryable = row.pushStatus !== "success";

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle} style={headerStyle}>
        <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Feedback detail</span>
      </ModalHeader>

      <ModalBody style={bodyStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
          <Stars value={row.rating} />
          <span style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{row.rating}/5</span>
          <span style={{ marginLeft: "auto" }}>
            <Badge color={meta.color} bg={meta.bg}>{meta.label}</Badge>
          </span>
        </div>

        <Section title="Comment">
          {row.comment ? (
            <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: C.text, whiteSpace: "pre-wrap" }}>
              {row.comment}
            </p>
          ) : (
            <Empty>No comment left.</Empty>
          )}
        </Section>

        {row.images?.length > 0 && (
          <Section title={`Photos (${row.images.length})`}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {row.images.map((src) => (
                // Opens full size in a tab rather than a nested lightbox inside a modal,
                // which traps focus and cannot be zoomed.
                <a key={src} href={src} target="_blank" rel="noreferrer">
                  <img
                    src={src}
                    alt=""
                    style={{ width: 96, height: 96, objectFit: "cover", borderRadius: 8, border: `1px solid ${C.border}` }}
                  />
                </a>
              ))}
            </div>
          </Section>
        )}

        <Section title="Customer">
          {row.customerName || row.customerPhone ? (
            <>
              <Row label="Name" value={row.customerName || "—"} />
              <Row label="Phone" value={row.customerPhone || "—"} />
              <Row
                label="Source"
                value={
                  row.contactSource === "customer"
                    ? "Typed on the feedback form — these details were not on the receipt"
                    : "From the receipt"
                }
              />
            </>
          ) : (
            <Empty>Walk-in — no contact details.</Empty>
          )}
        </Section>

        <Section title="Order">
          <Row label="Transaction" value={row.transactionNo || "—"} mono />
          <Row label="Order uid" value={row.orderUid} mono />
          <Row label="Branch" value={row.merchantBranchId || "—"} />
          <Row label="Received" value={new Date(row.createdAt).toLocaleString()} />
        </Section>

        <Section title="Odoo sync">
          <Row label="Status" value={meta.label} />
          <Row label="Attempts" value={`${row.pushAttempts} of ${row.maxPushAttempts}`} />
          {row.nextAttemptAt && row.pushStatus === "pending" && (
            <Row label="Next attempt" value={new Date(row.nextAttemptAt).toLocaleString()} />
          )}
          {row.pushedAt && <Row label="Synced at" value={new Date(row.pushedAt).toLocaleString()} />}
          {row.merchantFeedbackId && <Row label="Odoo review id" value={row.merchantFeedbackId} mono />}
          {row.pushError && (
            <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 8, background: C.bg, border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textMed, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.6 }}>
                Last response
              </div>
              <div style={{ fontSize: 12, color: C.text, fontFamily: "monospace", wordBreak: "break-word" }}>
                {row.pushError}
              </div>
            </div>
          )}

          {row.pushStatus === "stale" && (
            <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 8, background: "#FEE4E2", color: "#B42318", fontSize: 12.5 }}>
              Odoo never received the order this feedback belongs to, so it had nothing to attach
              the review to. Once that order has synced, press Sync now.
            </div>
          )}
        </Section>
      </ModalBody>

      <ModalFooter style={footerStyle}>
        {retryable && (
          <Button style={coralBtnStyle} disabled={syncing} onClick={() => onRetry([row.id])}>
            <RotateCw size={14} style={{ marginRight: 6 }} />
            {syncing ? "Queuing…" : "Sync now"}
          </Button>
        )}
        <Button style={ghostBtnStyle} onClick={toggle}>Close</Button>
      </ModalFooter>
    </Modal>
  );
};

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: C.textMed, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>
      {title}
    </div>
    {children}
  </div>
);

const Row = ({ label, value, mono }) => (
  <div style={{ display: "flex", gap: 12, padding: "5px 0", fontSize: 13 }}>
    <span style={{ color: C.textMed, minWidth: 120 }}>{label}</span>
    <span style={{ color: C.text, fontFamily: mono ? "monospace" : "inherit", wordBreak: "break-all" }}>
      {value}
    </span>
  </div>
);

const Empty = ({ children }) => (
  <p style={{ margin: 0, fontSize: 13, color: C.textLight }}>{children}</p>
);

const Stars = ({ value }) => (
  <span style={{ display: "inline-flex", gap: 2 }}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        size={20}
        color={n <= value ? (value <= 2 ? "#B42318" : C.coral) : C.border}
        fill={n <= value ? (value <= 2 ? "#B42318" : C.coral) : "none"}
      />
    ))}
  </span>
);

export default FeedbackDetailModal;
